import { makeStyles, Theme } from "@material-ui/core/styles";
import "bootstrap/dist/css/bootstrap.css";
import { useEffect, useRef, useState } from "react";
import Collapse from "reactstrap/lib/Collapse";
import Container from "reactstrap/lib/Container";
import Navbar from "reactstrap/lib/Navbar";
import NavbarBrand from "reactstrap/lib/NavbarBrand";
import NavbarToggler from "reactstrap/lib/NavbarToggler";
import NavLink from "reactstrap/lib/NavLink";
import { getAppState, useAppState } from "../../../../common/appState";
import { ARTICLES_URL } from "../../../../common/consts";
import { sleepAsync } from "../../../../common/functions";
import { useAbTest } from "../../../../common/hooks/useAbTest";
import { useDelayedState } from "../../../../common/hooks/useDelayedState";
import { useScreenSize } from "../../../../common/hooks/useScreenSize";
import { useUser } from "../../../../common/hooks/useUser";
import { spaceBetween } from "../../../../common/util/Array/spaceBetween";
import { keys } from "../../../../common/util/Object/keys";
import ShurikenProgress from "../../../shared/Animations/ShurikenProgress";
import { DarkLayer } from "../../../shared/DarkLayer";
import { ATargetBlank } from "../../../shared/Link/ATargetBlank";
import { A, Link } from "../../../shared/Link/LinkWithYouTube";
import { RightPanel } from "../../../shared/Panel/RightPanel";
import { zIndex } from "../zIndex";
import { LoginIcon } from "./components/LoginIcon";
import { SocialIcon } from "./components/SocialIcon";
import "./style.css";

const links: { name: string; url: string }[] = [
    { name: "Hiragana / Katakana", url: "/hiragana-katakana" },
    { name: "Grammar", url: "/grammar" },
    { name: "Vocab", url: "/vocabulary-quiz" },
    { name: "Kanji", url: "/kanji" },
    {
        name: "Reading",
        url: `${ARTICLES_URL}/jlpt-n3-reading-practice`,
    },
    { name: "Flashcard", url: "/flashcard" },
    { name: "Folktale", url: "/folktales" },
    { name: "Article", url: ARTICLES_URL },
    { name: "Video", url: `${ARTICLES_URL}/videos` },
    { name: "Game", url: "/ninja" },
];

function NavigationItems({
    closeToggle,
    setIsDonatePanelOpen,
    donateButtonText,
    isHamburger,
    screenWidth,
    isHeaderTwoLinesInPc,
}: {
    closeToggle: () => void;
    setIsDonatePanelOpen: (isOpen: boolean) => void;
    donateButtonText: string | undefined;
    isHamburger: boolean;
    screenWidth: number;
    isHeaderTwoLinesInPc: boolean;
}) {
    return (
        <ul
            className="navbar-nav flex-grow flexWrap"
            onClick={closeToggle}
            style={{
                fontSize:
                    !isHamburger && screenWidth < 1300 ? "14px" : undefined,
            }}
        >
            {links.map(l => {
                if (l.url.startsWith("https://")) {
                    return (
                        <NavLink
                            key={l.name}
                            tag={A}
                            className="text-light dropdown nowrap"
                            href={l.url}
                            style={{
                                transition: "all 500ms",
                                ...(isHeaderTwoLinesInPc
                                    ? { marginLeft: 10 }
                                    : {}),
                            }}
                        >
                            {l.name}
                        </NavLink>
                    );
                }
                return (
                    <NavLink
                        key={l.name}
                        tag={Link}
                        className="text-light dropdown nowrap"
                        to={l.url}
                        style={{
                            transition: "all 500ms",
                            ...(isHeaderTwoLinesInPc ? { marginLeft: 10 } : {}),
                        }}
                    >
                        {l.name}
                    </NavLink>
                );
            })}
            <NavLink
                className="text-light dropdown nowrap pointer"
                onClick={() => setIsDonatePanelOpen(true)}
                style={{
                    maxWidth: donateButtonText ? 200 : 0,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textAlign: isHamburger ? undefined : "center",
                    transition: "all 500ms",
                    ...(isHeaderTwoLinesInPc ? { marginLeft: 10 } : {}),
                }}
            >
                {donateButtonText}
            </NavLink>
        </ul>
    );
}

const donateButtonTexts = [
    "Donate",
    "Support",
    "Donate☕",
    "Support☕",
    "☕Donate",
    "☕Support",
] as const;

const donationPanelMessages = {
    "0": (
        <>
            <h2 style={{ marginBottom: 20 }}>Support Lingual Ninja!</h2>
            <p>
                If you enjoy using Lingual Ninja, please consider supporting its
                development. Your donation will help me continue improving the
                app and adding new features.
            </p>
        </>
    ),
    "1": (
        <>
            <h2 style={{ marginBottom: 20 }}>Support Lingual Ninja</h2>
            <p>
                Help keep this app free for learners around the world. If
                Lingual Ninja has helped your Japanese, a small tip goes a long
                way. Your support covers server costs and lets me keep making
                new content.
            </p>
        </>
    ),
    "2": (
        <>
            <h2 style={{ marginBottom: 20 }}>
                Made with care. Powered by you.
            </h2>
            <p>
                I’ve spent years crafting Lingual Ninja in my spare time. If
                you’ve found value here, please consider supporting my work.
                Your donation helps me keep improving lessons, fixing bugs, and
                shipping new features faster.
            </p>
        </>
    ),
    "3": (
        <>
            <h2 style={{ marginBottom: 20 }}>Help cover the basics</h2>
            <p>
                Server and database hosting costs around $90/month, plus ongoing
                development time. I want Lingual Ninja to stay free and fast for
                everyone. If it helps your learning, a small donation makes a
                real difference.
            </p>
        </>
    ),
    "4": (
        <>
            <h2 style={{ marginBottom: 20 }}>Fund the next lessons</h2>
            <p>
                Your donation directly supports upcoming content and performance
                upgrades. Back the roadmap and help me release new content more
                often—while keeping Lingual Ninja free for all learners.
            </p>
        </>
    ),
    "5": (
        <>
            <h2 style={{ marginBottom: 20 }}>Enjoying Lingual Ninja?</h2>
            <p>
                Drop a tip—no account, no pressure. If today’s lesson helped,
                consider a coffee-sized thank-you. Every bit keeps the lights on
                and the lessons coming.
            </p>
        </>
    ),
    "6": (
        <>
            <h2 style={{ marginBottom: 20 }}>
                Learn together. Support together.
            </h2>
            <p>
                Your donation keeps Lingual Ninja open for beginners and
                advanced learners alike. Together we can keep it accessible to
                everyone.
            </p>
        </>
    ),
    "7": (
        <>
            <h2 style={{ marginBottom: 20 }}>
                Your donation makes Lingual Ninja sustainable
            </h2>
            <p>
                To improve Lingual Ninja, I left my programming job in Japan and
                began working as a Japanese language teacher in the Philippines.
                This allows me to better understand the needs of learners and
                improve my teaching skills.
            </p>
            <p>
                However, since leaving the Japanese IT industry, my income has
                decreased, and paying for the server costs of this website has
                become challenging. The monthly usage fees for Microsoft Azure
                App Service and SQL Server exceed $90.
            </p>
            <p>
                Even a small donation can help keep Lingual Ninja free for
                everyone. Your support makes a real difference.
            </p>
        </>
    ),
} as const;
const donateButtonExplanations = {
    "0": "Click the button below!",
    "1": "Click the button below to donate!",
    "2": "Donate using the button below!",
    "3": "Click the button below to support Lingual Ninja!",
    "4": "Click below to proceed!",
    "5": "Support by clicking the button below!",
    "6": "Click the button below👇",
    "7": "Click the button below to donate👇",
    "8": "Donate using the button below👇",
    "9": "Click the button below to support Lingual Ninja👇",
    "10": "Click below to proceed👇",
    "11": "Support by clicking the button below👇",
    "12": "Click this button👇",
    "13": "",
} as const;

export default function NavMenu() {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLElement>(null);
    const hideAppBar = useHideAppBar();
    const [isDonatePanelOpen, setIsDonatePanelOpen] = useState(false);
    const {
        abTestKey: donateButtonText,
        abTestSuccess: donateButtonAbTestSuccess,
    } = useAbTest({
        testName: "DonateButtonText",
        keys: donateButtonTexts,
    });

    const { screenWidth } = useScreenSize();
    const isHamburger = screenWidth < 768;
    const headerHeight = useHeaderHeight(ref.current, isHamburger);

    useEffect(() => {
        const onScroll = () => {
            setIsOpen(false);
        };
        window.addEventListener("scroll", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
        };
    }, []);

    const [isDarkMode] = useAppState("isDarkMode");
    const c = useNavMenuStyles({
        hideAppBar,
        isOpen,
        headerHeight,
        isDarkMode,
    });

    const [isHeaderTwoLinesInPc, setIsHeaderTwoLinesInPc] = useState(false);
    useEffect(() => {
        if (isHeaderTwoLinesInPc) {
            return; // がたつきの繰り返しを防ぐため、一度TrueになったらずっとTrue
        }
        setIsHeaderTwoLinesInPc(headerHeight > 80 && !isHamburger);
    }, [headerHeight, isHamburger]);

    return (
        <>
            <header
                ref={ref}
                className={spaceBetween(c.header, "reverseDarkMode")}
            >
                <Navbar
                    variant="pills"
                    className={spaceBetween(
                        "navbar-inverse navbar-expand-md navbar-toggleable-md border-bottom box-shadow mb-3",
                        c.navBar
                    )}
                >
                    <Container
                        style={
                            isHamburger
                                ? undefined
                                : {
                                      marginLeft: 0,
                                      marginRight: 0,
                                      maxWidth: 3000,
                                      paddingLeft: 0,
                                  }
                        }
                    >
                        <NavbarBrand
                            tag={Link}
                            to="/"
                            onClick={() => {
                                setIsOpen(false);
                            }}
                            style={{
                                whiteSpace: "nowrap",
                                transition: "all 500ms",
                                ...(isHeaderTwoLinesInPc
                                    ? { marginRight: 0, marginLeft: 0 }
                                    : {}),
                            }}
                        >
                            <span
                                className={spaceBetween(
                                    "z-apps-title text-light",
                                    c.titleContainer
                                )}
                                style={{
                                    transition: "all 500ms",
                                    ...(isHeaderTwoLinesInPc
                                        ? { fontSize: "large" }
                                        : {}),
                                }}
                            >
                                Lingual Ninja
                            </span>
                        </NavbarBrand>
                        <Menus
                            isOpen={isOpen}
                            setIsOpen={setIsOpen}
                            screenWidth={screenWidth}
                            isHamburger={isHamburger}
                            setIsDonatePanelOpen={setIsDonatePanelOpen}
                            donateButtonText={donateButtonText}
                            isHeaderTwoLinesInPc={isHeaderTwoLinesInPc}
                        />
                    </Container>
                </Navbar>
            </header>
            <div className={c.spaceBelow} />
            <DarkLayer
                open={isOpen}
                onClick={() => {
                    setIsOpen(false);
                }}
                zIndex={zIndex.navMenu.smartphoneMenuOpenDarkLayer}
            />
            <DonationPanel
                isDonatePanelOpen={isDonatePanelOpen}
                setIsDonatePanelOpen={setIsDonatePanelOpen}
                abTestSuccess={donateButtonAbTestSuccess}
            />
        </>
    );
}

const topOverFlow = 50;
const useNavMenuStyles = makeStyles<
    Theme,
    {
        hideAppBar: boolean;
        isOpen: boolean;
        headerHeight: number;
        isDarkMode: boolean | null;
    }
>({
    titleContainer: {
        whiteSpace: "nowrap",
        fontWeight: "bold",
    },
    header: ({ hideAppBar, isOpen }) => ({
        top: hideAppBar ? -(100 + topOverFlow) : -topOverFlow,
        zIndex: zIndex.navMenu.header(isOpen),
    }),
    navBar: ({ isDarkMode }) => ({
        backgroundColor: isDarkMode ? "black" : "#222222",
        marginTop: topOverFlow,
    }),
    spaceBelow: ({ headerHeight }) => ({
        height: headerHeight,
        marginBottom: 14,
        transition: "all 300ms",
    }),
});

function DonationPanel({
    isDonatePanelOpen,
    setIsDonatePanelOpen,
    abTestSuccess,
}: {
    isDonatePanelOpen: boolean;
    setIsDonatePanelOpen: (isOpen: boolean) => void;
    abTestSuccess: () => void;
}) {
    const { delayedOpen } = useDelayedState(
        isDonatePanelOpen,
        isOpen => isOpen,
        1000
    );

    return (
        <RightPanel
            open={isDonatePanelOpen}
            onClose={() => setIsDonatePanelOpen(false)}
            panelWidth={400}
        >
            {delayedOpen && (
                <DonationPanelContent abTestSuccess={abTestSuccess} />
            )}
        </RightPanel>
    );
}

function DonationPanelContent({
    abTestSuccess,
}: {
    abTestSuccess: () => void;
}) {
    const {
        abTestKey: donatePanelMessageKey,
        abTestSuccess: donatePanelMessageAbTestSuccess,
    } = useAbTest({
        testName: "DonatePanelMessage",
        keys: keys(donationPanelMessages),
    });
    const {
        abTestKey: donateButtonExplanationKey,
        abTestSuccess: donateButtonExplanationAbTestSuccess,
    } = useAbTest({
        testName: "DonateButtonExplanation",
        keys: keys(donateButtonExplanations),
    });

    if (!donatePanelMessageKey || !donateButtonExplanationKey) {
        return <ShurikenProgress size={30} style={{ marginTop: 50 }} />;
    }

    return (
        <div style={{ padding: 20 }}>
            {donationPanelMessages[donatePanelMessageKey]}
            {donateButtonExplanations[donateButtonExplanationKey] && (
                <>
                    {donateButtonExplanations[donateButtonExplanationKey]}
                    <br />
                </>
            )}
            <ATargetBlank
                href="https://buymeacoffee.com/lingualninja"
                onClick={() => {
                    abTestSuccess();
                    donatePanelMessageAbTestSuccess();
                    donateButtonExplanationAbTestSuccess();
                }}
            >
                <img
                    src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
                    alt="Buy Me A Coffee"
                    style={{ height: 60, width: 217 }}
                    className="hoverScale05"
                />
            </ATargetBlank>
        </div>
    );
}

function Menus({
    isOpen,
    setIsOpen,
    screenWidth,
    isHamburger,
    setIsDonatePanelOpen,
    donateButtonText,
    isHeaderTwoLinesInPc,
}: {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    screenWidth: number;
    isHamburger: boolean;
    setIsDonatePanelOpen: (isOpen: boolean) => void;
    donateButtonText: string | undefined;
    isHeaderTwoLinesInPc: boolean;
}) {
    const c = useMenusStyles();
    const iconMarginLeft = screenWidth / 30;
    const { user, isUserFetchDone } = useUser();

    return (
        <>
            <div className={c.iconsArea}>
                <NavbarToggler
                    onClick={() => {
                        setIsOpen(!isOpen);
                    }}
                    className="mr-2"
                />
                {isHamburger && (
                    <>
                        <SocialIcon
                            containerStyle={{
                                marginLeft: isOpen
                                    ? 0
                                    : `calc(${iconMarginLeft}px - 0.25rem)`, // Hamburger's right margin is 0.5rem,,
                            }}
                            isOpenHamburger={isOpen && isHamburger}
                            transitionClass={"t500ms"}
                        />
                        <LoginIcon
                            containerStyle={{
                                marginLeft: `calc(${iconMarginLeft}px + 0.25rem)`, // Hamburger's right margin is 0.5rem,
                            }}
                            isOpenHamburger={isOpen && isHamburger}
                            transitionClass={"t500ms"}
                            user={user}
                            isUserFetchDone={isUserFetchDone}
                        />
                    </>
                )}
            </div>
            <Collapse
                className="d-md-inline-flex flex-md-row-reverse"
                isOpen={isOpen}
                navbar
                style={
                    isHamburger
                        ? undefined
                        : {
                              minWidth: screenWidth - 400,
                              justifyContent: "center",
                          }
                }
            >
                <NavigationItems
                    closeToggle={() => {
                        setIsOpen(false);
                    }}
                    setIsDonatePanelOpen={setIsDonatePanelOpen}
                    donateButtonText={donateButtonText}
                    isHamburger={isHamburger}
                    screenWidth={screenWidth}
                    isHeaderTwoLinesInPc={isHeaderTwoLinesInPc}
                />
            </Collapse>
            {!isHamburger && (
                <>
                    <SocialIcon
                        containerStyle={{
                            marginLeft: iconMarginLeft,
                        }}
                        transitionClass={"t1s"}
                    />
                    <LoginIcon
                        containerStyle={{
                            marginLeft: Math.min(iconMarginLeft, 25),
                        }}
                        transitionClass={"t1s"}
                        user={user}
                        isUserFetchDone={isUserFetchDone}
                    />
                </>
            )}
        </>
    );
}
const useMenusStyles = makeStyles({
    iconsArea: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
    },
});

function useHeaderHeight(
    headerElement: HTMLElement | null,
    isHamburger: boolean
) {
    const [headerHeight, setHeaderHeight] = useAppState("headerHeight");

    useEffect(() => {
        if (!headerElement) {
            return;
        }

        if (isHamburger) {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            setHeaderHeight(headerElement.clientHeight - (14 + topOverFlow));
        });

        resizeObserver.observe(headerElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [headerElement, setHeaderHeight, isHamburger]);

    return headerHeight;
}

export function scrollToElement(
    element: HTMLElement | null,
    noSmooth?: boolean,
    headerShown?: boolean,
    offset?: number
) {
    if (headerShown) {
        const { headerHeight } = getAppState();
        const elementTop = element?.getBoundingClientRect()?.top || 0;

        window.scrollTo({
            top: window.scrollY + elementTop - headerHeight - (offset ?? 0),
            behavior: noSmooth ? undefined : "smooth",
        });
        return;
    }

    hideHeaderTemporarily();
    element?.scrollIntoView(
        noSmooth
            ? true
            : {
                  behavior: "smooth",
              }
    );
}

export let hideHeaderTemporarily: () => void = () => {};
export let showHeader: () => void = () => {};

let previousScrollY = 0;
let isHidden = false;
let appBarTimer = 0;

function useHideAppBar() {
    const [isAppBarHidden, setIsAppBarHidden] = useAppState("isAppBarHidden");

    showHeader = () => {
        setIsAppBarHidden(false);
        isHidden = false;
    };

    hideHeaderTemporarily = () => {
        setIsAppBarHidden(true);
        isHidden = true;

        if (appBarTimer > 0) {
            clearTimeout(appBarTimer);
        }
        appBarTimer = window.setTimeout(showHeader, 5000);
    };

    useEffect(() => {
        const scrollHandler = async () => {
            await sleepAsync(10); // In case where starting point's scrollY is 0

            const isRapidScroll = window.scrollY > previousScrollY + 500;
            previousScrollY = window.scrollY;

            if (window.scrollY <= 0) {
                setIsAppBarHidden(false);
                return;
            }

            if (isHidden) {
                return;
            }

            if (!isRapidScroll) {
                return;
            }

            hideHeaderTemporarily();
        };

        window.addEventListener("scroll", scrollHandler);
        return () => {
            window.removeEventListener("scroll", scrollHandler);
        };
    }, []);

    return isAppBarHidden;
}

